import {
  AppstoreOutlined,
  DashboardOutlined,
  FileTextOutlined,
  SettingOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  ShareAltOutlined,
  UserOutlined,
  ShopOutlined,
  GlobalOutlined,
  MailOutlined,
  EnvironmentOutlined,
  BulbOutlined,
  BellOutlined,
  RobotOutlined,
  CustomerServiceOutlined,
  UserAddOutlined,
  TrophyOutlined,
  FileProtectOutlined,
  AccountBookOutlined,
  DollarOutlined,
  CheckSquareOutlined,
  BarChartOutlined,
  FileExcelOutlined,
  SafetyOutlined,
  LockOutlined,
  ScheduleOutlined,
  FunnelPlotOutlined,
  ToolOutlined,
  ShoppingCartOutlined,
  AuditOutlined,
  CarOutlined,
  PlayCircleOutlined,
  InboxOutlined,
  FundOutlined,
  ScanOutlined,
  NodeIndexOutlined as TrackingOutlined,
  ReadOutlined,
  SolutionOutlined,
  MessageOutlined,
  FileSearchOutlined,
  SendOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { ProLayout } from '@ant-design/pro-components';
import { Modal, Badge } from 'antd';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

import { inboxApi, type InboxCounts } from '@/api/services';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { LunaAssistant } from '@/components/LunaAssistant';
import { NotificationBell } from '@/components/NotificationBell';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { useAuthStore } from '@/stores/auth.store';

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();
  const { showWarning, secondsLeft, extendSession } = useAutoLogout();

  // Mobile responsive: collapse sidebar on small screens
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebarCollapsed(mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Sidebar badge counts (unprocessed M3 inbox items) ──────────────────────
  const [inboxCounts, setInboxCounts] = useState<InboxCounts>({
    contactsUnread: 0, aiLeadsNew: 0, rfqNew: 0, subscribersNew: 0,
  });

  useEffect(() => {
    let alive = true;
    const fetchCounts = () => {
      inboxApi.counts()
        .then((res) => { if (alive && res?.data) setInboxCounts(res.data); })
        .catch(() => { /* badges are best-effort; ignore errors */ });
    };
    fetchCounts();
    const timer = window.setInterval(fetchCounts, 60_000);
    // Pages dispatch 'inbox:refresh' after processing an item so badges shrink immediately.
    window.addEventListener('inbox:refresh', fetchCounts);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener('inbox:refresh', fetchCounts);
    };
  }, []);

  // Refresh counts on navigation so badges shrink right after processing items.
  useEffect(() => {
    inboxApi.counts()
      .then((res) => { if (res?.data) setInboxCounts(res.data); })
      .catch(() => { /* ignore */ });
  }, [location.pathname]);

  /** Render a menu label with a small highlighted count badge when count > 0. */
  const withBadge = (label: string, count: number): React.ReactNode =>
    count > 0
      ? (
        <span>
          {label}
          <Badge
            count={count}
            size="small"
            style={{ marginInlineStart: 6, boxShadow: 'none' }}
          />
        </span>
      )
      : label;

  const m3Total =
    inboxCounts.contactsUnread + inboxCounts.aiLeadsNew + inboxCounts.rfqNew + inboxCounts.subscribersNew;

  const menuRoutes = {
    routes: [
      {
        path: '/',
        name: t('nav.dashboard'),
        icon: <DashboardOutlined />,
      },
      {
        path: '/m1',
        name: t('nav.m1'),
        icon: <AppstoreOutlined />,
        routes: [
          { path: '/m1/products', name: t('nav.m1_products') },
          { path: '/m1/documents', name: t('nav.m1_documents') },
          { path: '/m1/import', name: t('nav.m1_import') },
          { path: '/m1/products/batch', name: t('nav.m1_batch'), icon: <FileExcelOutlined /> },
          { path: '/m1/param-standards', name: t('nav.m1_paramStandards', '参数标准字典'), icon: <AuditOutlined /> },
        ],
      },
      {
        path: '/m2',
        name: t('nav.m2'),
        icon: <ShopOutlined />,
        routes: [
          { path: '/m2/supplier-applications', name: t('nav.m2_applications', 'Pending Applications'), icon: <Badge count={0} size="small" /> },
          { path: '/m2/suppliers', name: t('nav.m2_suppliers') },
          { path: '/m2/submissions', name: t('nav.m2_submissions') },
          { path: '/m2/purchase-orders', name: t('nav.m2_purchase_orders') },
          { path: '/m2/update-requests', name: t('nav.m2_update_requests', '产品更新申请') },
          { path: '/m2/suppliers/batch', name: t('nav.m2_batch'), icon: <FileExcelOutlined /> },
        ],
      },
      {
        path: '/m3',
        name: withBadge(t('nav.m3'), m3Total),
        icon: <GlobalOutlined />,
        routes: [
          { path: '/m3/blogs', name: t('nav.m3_blogs'), icon: <FileTextOutlined /> },
          { path: '/m3/solutions', name: t('nav.m3_solutions'), icon: <FileSearchOutlined /> },
          { path: '/m3/courses', name: t('nav.m3_courses'), icon: <ReadOutlined /> },
          { path: '/m3/jobs', name: t('nav.m3_jobs'), icon: <SolutionOutlined /> },
          { path: '/m3/contacts', name: withBadge(t('nav.m3_contacts'), inboxCounts.contactsUnread), icon: <MessageOutlined /> },
          { path: '/m3/ai-contacts', name: withBadge(t('nav.m3_ai_contacts', 'AI接待'), inboxCounts.aiLeadsNew), icon: <RobotOutlined /> },
          { path: '/m3/live-chat', name: t('nav.m3_livechat', '实时客服'), icon: <CustomerServiceOutlined /> },
          { path: '/m3/rfq', name: withBadge(t('nav.m3_rfq'), inboxCounts.rfqNew), icon: <SendOutlined /> },
          { path: '/m3/subscribers', name: withBadge(t('nav.m3_subscribers'), inboxCounts.subscribersNew), icon: <MailOutlined /> },
        ],
      },
      {
        path: '/m4',
        name: t('nav.m4'),
        icon: <VideoCameraOutlined />,
        routes: [
          { path: '/m4/projects', name: t('nav.m4_projects') },
          { path: '/m4/templates', name: t('nav.m4_templates') },
          { path: '/m4/render-queue', name: t('nav.m4_renderQueue') },
        ],
      },
      {
        path: '/m5',
        name: t('nav.m5'),
        icon: <ShareAltOutlined />,
        routes: [
          { path: '/m5/accounts', name: t('nav.m5_accounts') },
          { path: '/m5/posts', name: t('nav.m5_posts') },
          { path: '/m5/analytics', name: t('nav.m5_analytics') },
        ],
      },
      {
        path: '/m6',
        name: t('nav.m6'),
        icon: <UserOutlined />,
        routes: [
          { path: '/m6/pipeline', name: t('nav.m6_pipeline') },
          { path: '/m6/customers', name: t('nav.m6_customers') },
          { path: '/m6/customers/batch', name: t('nav.m6_customersBatch') },
          { path: '/m6/customer-map', name: t('nav.m6_customerMap') },
          { path: '/m6/inquiries', name: t('nav.m6_inquiries') },
          { path: '/m6/followups', name: t('nav.m6_followups') },
          { path: '/m6/reminders', name: t('nav.m6_reminders'), icon: <BellOutlined /> },
          { path: '/m6/quotes', name: t('nav.m6_quotes'), icon: <FileTextOutlined /> },
          { path: '/m6/leads', name: t('nav.m6_leads'), icon: <UserAddOutlined /> },
          { path: '/m6/opportunities', name: t('nav.m6_opportunities'), icon: <TrophyOutlined /> },
          { path: '/m6/contracts', name: t('nav.m6_contracts'), icon: <FileProtectOutlined /> },
          { path: '/m6/invoices', name: t('nav.m6_invoices'), icon: <AccountBookOutlined /> },
          { path: '/m6/payments', name: t('nav.m6_payments'), icon: <DollarOutlined /> },
          { path: '/m6/tasks', name: t('nav.m6_tasks'), icon: <CheckSquareOutlined /> },
          { path: '/m6/analytics', name: t('nav.m6_analytics'), icon: <BarChartOutlined /> },
          { path: '/m6/crm-products', name: t('nav.m6_crmProducts', 'CRM产品目录'), icon: <ShopOutlined /> },
          { path: '/m6/customers/public-sea', name: t('nav.m6_publicSea'), icon: <FunnelPlotOutlined /> },
          { path: '/m6/pool-settings', name: t('nav.m6_poolSettings'), icon: <ToolOutlined /> },
        ],
      },
      {
        path: '/m6-mckay',
        name: t('nav.m6_mckay'),
        icon: <BulbOutlined />,
        routes: [
          { path: '/m6/smart-input', name: t('nav.m6_smartInput') },
          { path: '/m6/companies', name: t('nav.m6_companies') },
          { path: '/m6/ai-models', name: t('nav.m6_aiModels'), icon: <RobotOutlined /> },
        ],
      },
      {
        path: '/m6-visit',
        name: t('nav.visitManagement'),
        icon: <EnvironmentOutlined />,
        routes: [
          { path: '/m6/checkin', name: t('nav.m6_checkin') },
          { path: '/m6/visit-checkins', name: t('nav.m6_visitCheckins') },
        ],
      },
      {
        path: '/m6-email',
        name: t('nav.emailManagement'),
        icon: <MailOutlined />,
        routes: [
          { path: '/m6/email', name: t('nav.m6_emailClient'), icon: <MailOutlined /> },
          { path: '/m6/email-approvals', name: '待审邮件' },
          { path: '/m6/email-templates', name: t('nav.m6_emailTemplates') },
          { path: '/m6/email-logs', name: t('nav.m6_emailLogs') },
        ],
      },
      {
        path: '/m6-security',
        name: t('nav.m6_security'),
        icon: <SafetyOutlined />,
        routes: [
          { path: '/m6/2fa', name: t('nav.m6_2fa'), icon: <SafetyOutlined /> },
          { path: '/m6/ip-whitelist', name: t('nav.m6_ipWhitelist'), icon: <LockOutlined /> },
          { path: '/m6/pipl', name: t('nav.m6_pipl'), icon: <FileProtectOutlined /> },
        ],
      },
      {
        path: '/m6-ops',
        name: t('nav.m6_ops'),
        icon: <BarChartOutlined />,
        routes: [
          { path: '/m6/ai-quality', name: t('nav.m6_aiQuality'), icon: <RobotOutlined /> },
          { path: '/m6/report-schedules', name: t('nav.m6_reportSchedules'), icon: <ScheduleOutlined /> },
        ],
      },
      {
        path: '/m7',
        name: t('nav.m7'),
        icon: <ShoppingCartOutlined />,
        routes: [
          { path: '/m7/sales-orders', name: t('nav.m7_salesOrders') },
        ],
      },
      {
        path: '/m8',
        name: t('nav.m8'),
        icon: <AuditOutlined />,
        routes: [
          { path: '/m8/inspections', name: t('nav.m8_inspections') },
        ],
      },
      {
        path: '/m9',
        name: t('nav.m9'),
        icon: <CarOutlined />,
        routes: [
          { path: '/m9/shipments', name: t('nav.m9_shipments') },
        ],
      },
      {
        path: '/m10',
        name: t('nav.m10'),
        icon: <RobotOutlined />,
        routes: [
          { path: '/m10/sdr', name: t('nav.m10_sdr'), icon: <DashboardOutlined /> },
          { path: '/m10/department', name: 'AI 业务部门', icon: <RobotOutlined /> },
          { path: '/m10/sdr/leads', name: t('nav.m10_leads'), icon: <UserAddOutlined /> },
          { path: '/m10/strategies', name: t('nav.m10_strategies'), icon: <PlayCircleOutlined /> },
        ],
      },
      {
        path: '/m11',
        name: t('nav.m11'),
        icon: <InboxOutlined />,
        routes: [
          { path: '/m11/inventory', name: t('nav.m11_inventory') },
          { path: '/m11/locator', name: '全球库存定位/扫码' },
          { path: '/m11/bin-settings', name: '仓库库位配置' },
          { path: '/m11/assets', name: '固定资产台账' },
          { path: '/m11/goods-receipts', name: '入库单' },
          { path: '/m11/bom', name: 'BOM匹配' },
          { path: '/m11/projects', name: '项目成本中心' },
          { path: '/m11/issue-notes', name: '项目领用/退库' },
        ],
      },
      {
        path: '/m12',
        name: t('nav.m12'),
        icon: <FundOutlined />,
        routes: [
          { path: '/m12/finance', name: t('nav.m12_finance') },
          { path: '/m12/ar', name: t('nav.m12_ar') },
          { path: '/m12/ap', name: t('nav.m12_ap') },
          { path: '/m12/payment-gateway', name: t('nav.m12_payment_gateway', '在线支付网关') },
        ],
      },
      {
        path: '/m13',
        name: t('nav.m13'),
        icon: <ScanOutlined />,
        routes: [
          { path: '/m13/ocr', name: t('nav.m13_ocr') },
        ],
      },
      {
        path: '/m14',
        name: t('nav.m14'),
        icon: <TrackingOutlined />,
        routes: [
          { path: '/m14/tracking', name: t('nav.m14_tracking') },
        ],
      },
      {
        path: '/outbound',
        name: t('nav.outbound'),
        icon: <PhoneOutlined />,
        routes: [
          { path: '/outbound/collect', name: t('nav.outbound_collect') },
          { path: '/outbound/review', name: t('nav.outbound_review') },
          { path: '/outbound/campaigns', name: t('nav.outbound_campaigns') },
          { path: '/outbound/lines', name: t('nav.outbound_lines', '线路中心') },
          { path: '/outbound/dialogue', name: t('nav.outbound_dialogue') },
          { path: '/outbound/calls', name: t('nav.outbound_calls') },
        ],
      },
      {
        path: '/system',
        name: t('nav.system'),
        icon: <SettingOutlined />,
        routes: [
          { path: '/system/users', name: t('nav.system_users') },
        ],
      },
    ],
  };

  return (
    <>
    <Modal
      open={showWarning}
      title={t('autoLogout.title')}
      okText={t('autoLogout.extend')}
      cancelText={t('autoLogout.logout')}
      onOk={extendSession}
      onCancel={logout}
      closable={false}
      maskClosable={false}
    >
      <p>{t('autoLogout.body', { seconds: secondsLeft })}</p>
      <p style={{ color: '#888', fontSize: 13 }}>{t('autoLogout.hint')}</p>
    </Modal>
    <ProLayout
      title={t('appTitle')}
      logo={false}
      layout="mix"
      fixSiderbar
      fixedHeader
      collapsed={sidebarCollapsed}
      onCollapse={setSidebarCollapsed}
      breakpoint="lg"
      route={menuRoutes}
      location={{ pathname: location.pathname }}
      menuFooterRender={() =>
        !sidebarCollapsed ? (
          <div style={{ padding: '12px 16px 16px', borderTop: '1px solid rgba(0,0,0,0.06)', color: '#bbb', fontSize: 11, lineHeight: 1.8 }}>
            <div style={{ fontWeight: 600, color: '#999' }}>v{__APP_VERSION__}</div>
            <div>{__BUILD_TIME__}</div>
            <div>#{__GIT_HASH__}</div>
          </div>
        ) : null
      }
      menuItemRender={(item, dom) => (
        <a
          onClick={() => {
            if (item.path) {
              navigate(item.path);
              // Auto-collapse sidebar after navigation on mobile
              if (isMobile) setSidebarCollapsed(true);
            }
          }}
        >
          {dom}
        </a>
      )}
      avatarProps={{
        icon: <TeamOutlined />,
        title: user?.name ?? t('admin'),
        render: (_props, defaultDom) => defaultDom,
      }}
      actionsRender={() => [
        <NotificationBell key="notifications" />,
        <LanguageSwitcher key="lang" />,
        <a key="logout" onClick={logout} style={{ marginLeft: 8 }}>
          {t('logout')}
        </a>,
      ]}
    >
      <div
        style={{
          minWidth: 0,
          paddingBottom: isMobile
            ? 'calc(56px + env(safe-area-inset-bottom))'
            : 0,
        }}
      >
        <Outlet />
      </div>
    </ProLayout>
    <LunaAssistant />

    {/* Mobile bottom navigation bar */}
    {isMobile && (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 'calc(56px + env(safe-area-inset-bottom))',
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxSizing: 'border-box',
        zIndex: 1000,
        background: '#fff', borderTop: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
      }}>
        {[
          { path: '/', icon: <DashboardOutlined />, label: '首页' },
          { path: '/m6/customers', icon: <UserOutlined />, label: '客户' },
          { path: '/m6/tasks', icon: <CheckSquareOutlined />, label: '任务' },
          { path: '/m6/smart-input', icon: <RobotOutlined />, label: '录入' },
          { path: '/m6/followups', icon: <MailOutlined />, label: '跟进' },
        ].map(({ path, icon, label }) => {
          const active = location.pathname === path || location.pathname.startsWith(path + '/');
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              style={{
                flex: 1, height: '100%', border: 'none', background: 'none', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 2, color: active ? '#1a56db' : '#6b7280', fontSize: 20,
              }}
            >
              {icon}
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>{label}</span>
            </button>
          );
        })}
      </div>
    )}
    </>
  );
};
